import { Project } from '@/stores/ProjectStore';
import { FiscalYear } from '@/types/fiscalYear';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getMainStatusStyle, getProjectStatus } from '../statusUtils';
import { Link } from 'react-router-dom';
import { Routes } from '@/CSIRouter';

interface LandingPageHeaderProps {
  organizationName: string;
  fiscalYears: FiscalYear[] | undefined;
  filteredProjects: Project[];
  canManageProjs: boolean;
  onProjectClick: (projectId: string) => void;
}

export function LandingPageHeader({
  organizationName,
  fiscalYears,
  filteredProjects,
  canManageProjs,
  onProjectClick,
}: LandingPageHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      pt: 'clamp(20px, 3vw, 30px)',
      paddingLeft: 'clamp(32px, 2.8vw, 54px)',
      paddingRight: 'clamp(44px, 3.8vw, 72px)',
      gap: 'clamp(20px, 2vw, 40px)',
      mb: 3
    }} >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          {/* Left side: Organization name and Projects title */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              pointerEvents: 'auto',
              ml: 'clamp(30px, 2.75vw, 55px)',
            }}
          >
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component='h1'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(24px, 1.85vw, 35px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
              mb: 'clamp(4px, 0.4vw, 8px)',
            }}
          >
            {organizationName}
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component='h2'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(45px, 3.5vw, 68px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
            }}
          >
            Projects
          </Typography>
        </Box>

        {/* Right side: Recently Viewed and New Project Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 'clamp(20px, 2vw, 40px)',
            ml: 'clamp(40px, 6vw, 120px)',
          }}
        >
          {/* Recently Viewed Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(4px, 0.5vw, 8px)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(12px, 1.04vw, 16px)',
                color: 'rgba(14, 70, 63, 1)',
              }}
            >
              Recently Viewed
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(8px, 0.8vw, 12px)',
              }}
            >
              {filteredProjects.slice(0, 3).map((project) => {
                const fiscalYearObj = fiscalYears?.find(
                  (fy) => fy.id === project.fiscalYear,
                );
                const fiscalYearDisplay = fiscalYearObj
                  ? fiscalYearObj.name.replace('FY ', 'FY - ')
                  : 'FY - N/A';

                // Get project status
                const { mainStatus } = getProjectStatus(project);
                const statusStyle = getMainStatusStyle(mainStatus);

                return (
                  <Box
                    key={project.id}
                    onClick={() => onProjectClick(project.id)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: 'clamp(160px, 13.5vw, 230px)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  >
                    {/* Top Row - Project Title and Fiscal Year */}
                    <Box
                      sx={{
                        backgroundColor: 'rgba(14, 70, 63, 1)',
                        padding: 'clamp(5px, 0.6vh, 9px) clamp(8px, 0.8vw, 14px)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'clamp(4px, 0.4vw, 8px)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: 'clamp(10px, 0.91vw, 14px)',
                          color: '#FFFFFF',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {project.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: 'clamp(8px, 0.72vw, 11px)',
                          color: '#FFFFFF',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fiscalYearDisplay}
                      </Typography>
                    </Box>

                    {/* Bottom Row - Status Tag */}
                    <Box
                      sx={{
                        backgroundColor: 'rgba(27, 84, 77, 1)',
                        padding: 'clamp(4px, 0.5vh, 7px) clamp(8px, 0.8vw, 14px)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: statusStyle.bg,
                          borderRadius: 'clamp(3px, 0.21vw, 4px)',
                          px: 'clamp(4px, 0.31vw, 6px)',
                          py: 'clamp(1px, 0.1vw, 2px)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: 'clamp(10px, 0.625vw, 12px)',
                            color: statusStyle.color,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {mainStatus}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* New Project Button - Conditionally visible based on permissions */}
        {canManageProjs && (
          <Button
            variant='contained'
            component={Link}
            to={'../' + Routes.ProjectOverview.path}
            disableRipple
            sx={{
              backgroundColor: 'rgba(224, 189, 97, 1) !important',
              color: 'rgba(11, 54, 49, 1) !important',
              border: '3px solid rgba(27, 84, 77, 0.35)',
              borderRadius: 'clamp(16px, 1.5vw, 24px) !important',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(12px, 1vw, 16px)',
              padding: 'clamp(8px, 0.8vw, 11px) clamp(20px, 1.6vw, 24px)',
              ml: 'clamp(4px, 0.4vw, 8px)',
              mr: 'clamp(4px, 0.4vw, 8px)',
              mt: 'clamp(4px, 0.4vw, 8px)',
              minHeight: 'unset',
              lineHeight: 1,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(214, 179, 87, 1) !important',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'rgba(204, 169, 77, 1) !important',
                boxShadow: 'none',
              },
              '&:focus': {
                boxShadow: 'none',
              },
            }}
          >
            <span style={{ fontSize: '1.3em', marginRight: '4px' }}>+</span> 
              New Project
          </Button>
        )}
      </Box>
    </Box>
  );
}
